/**
 * Attention Detail — Rich widget showing activities that require attention
 *
 * Displayed side-by-side with IssuesOverview below the risky shipments table.
 * Features category tabs, deduplicated item list, and paginated navigation.
 *
 * Duplicate activities (same type + container) are grouped — only the most
 * recent occurrence is shown, with an occurrence count badge.
 */

import { useState, useMemo } from 'react';
import { File06, Globe02, AlertCircle, Zap, ChevronLeft, ChevronRight, LayersThree01 } from '@untitledui/icons';
import { Button } from '@/components/base/buttons/button';
import { useAttentionRequired } from '@/hooks/api/use-attention-required';
import type { BackendActivity } from '@/data/api/activities';
import { Badge } from '@/components/base/badges/badges';
import { ExpandableActivityCard } from '@/components/application/containers/timeline/expandable-activity-card';

const CATEGORY_TABS = [
    { id: 'all', label: 'All' },
    { id: 'document', label: 'Documenti', icon: File06 },
    { id: 'tracking', label: 'Tracking', icon: Globe02 },
    { id: 'agent', label: 'Sistema', icon: Zap },
    { id: 'other', label: 'Altro', icon: AlertCircle },
] as const;

type CategoryTab = typeof CATEGORY_TABS[number]['id'];

const PAGE_SIZE = 5;

const KNOWN_CATEGORIES = new Set(['document', 'tracking', 'agent']);

/** Map activity to display category — unknown categories fall into 'other' */
function getDisplayCategory(activity: BackendActivity): string {
    if (activity.activity_type === 'cross_check_executed') return 'document';
    const cat = activity.activity_category;
    return KNOWN_CATEGORIES.has(cat) ? cat : 'other';
}

interface GroupedActivity {
    activity: BackendActivity;
    occurrences: number;
}

/**
 * Deduplicate activities by grouping on (activity_type + expedition_id).
 * Keeps the most recent activity per group (input is already sorted by created_at desc).
 */
function deduplicateActivities(activities: BackendActivity[]): GroupedActivity[] {
    const groups = new Map<string, GroupedActivity>();

    for (const activity of activities) {
        // Build a grouping key — activities with same type on same container are duplicates.
        // Activities without expedition_id are always unique.
        const key = activity.expedition_id
            ? `${activity.activity_type}::${activity.expedition_id}`
            : activity.activity_id;

        const existing = groups.get(key);
        if (existing) {
            existing.occurrences += 1;
        } else {
            groups.set(key, { activity, occurrences: 1 });
        }
    }

    return Array.from(groups.values());
}

/** Compute visible page numbers with ellipsis gaps */
function getPageNumbers(current: number, total: number): (number | 'ellipsis')[] {
    if (total <= 7) {
        return Array.from({ length: total }, (_, i) => i);
    }

    const pages: (number | 'ellipsis')[] = [];
    // Always show first page
    pages.push(0);

    if (current > 2) pages.push('ellipsis');

    // Pages around current
    const start = Math.max(1, current - 1);
    const end = Math.min(total - 2, current + 1);
    for (let i = start; i <= end; i++) {
        pages.push(i);
    }

    if (current < total - 3) pages.push('ellipsis');

    // Always show last page
    pages.push(total - 1);

    return pages;
}

interface AttentionDetailProps {
    startDate?: string | null;
    endDate?: string | null;
}

export function AttentionDetail({ startDate, endDate }: AttentionDetailProps) {
    const [currentPage, setCurrentPage] = useState(0);
    const { data, isLoading } = useAttentionRequired(500, startDate, endDate);
    const [activeTab, setActiveTab] = useState<CategoryTab>('all');
    const [showAllOccurrences, setShowAllOccurrences] = useState(false);

    const activities = data?.activities ?? [];

    // Deduplicate: group by (activity_type + expedition_id), or show all
    const groupedActivities = useMemo(() => {
        if (showAllOccurrences) {
            return activities.map(a => ({ activity: a, occurrences: 1 }));
        }
        return deduplicateActivities(activities);
    }, [activities, showAllOccurrences]);

    const filteredGroups = useMemo(() => {
        if (activeTab === 'all') return groupedActivities;
        return groupedActivities.filter(g => getDisplayCategory(g.activity) === activeTab);
    }, [groupedActivities, activeTab]);

    // Distinct count for header badge
    const distinctCount = groupedActivities.length;

    const totalPages = Math.max(1, Math.ceil(filteredGroups.length / PAGE_SIZE));
    const pageGroups = filteredGroups.slice(
        currentPage * PAGE_SIZE,
        (currentPage + 1) * PAGE_SIZE,
    );

    const handleTabChange = (tab: CategoryTab) => {
        setActiveTab(tab);
        setCurrentPage(0);
    };

    if (distinctCount === 0 && !isLoading) return null;

    if (isLoading) {
        return (
            <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                <div className="animate-pulse flex items-center gap-2">
                    <div className="h-5 w-40 bg-tertiary/20 rounded" />
                </div>
            </div>
        );
    }

    const pageNumbers = getPageNumbers(currentPage, totalPages);

    return (
        <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                    <AlertCircle className="size-5 text-warning-600" />
                    <h3 className="text-lg font-semibold text-primary">Requires Attention</h3>
                    <Badge color="warning" size="sm">{distinctCount}</Badge>
                </div>
                <Button
                    size="sm"
                    color={showAllOccurrences ? 'primary' : 'secondary'}
                    onClick={() => { setShowAllOccurrences(v => !v); setCurrentPage(0); }}
                    iconLeading={LayersThree01}
                >
                    {showAllOccurrences ? 'Deduplica' : 'Mostra tutto'}
                </Button>
            </div>

            {/* Category tabs */}
            <div className="flex items-center gap-1 mb-4 border-b border-secondary pb-2">
                {CATEGORY_TABS.map(tab => {
                    const count = tab.id === 'all'
                        ? groupedActivities.length
                        : groupedActivities.filter(g => getDisplayCategory(g.activity) === tab.id).length;
                    return (
                        <button
                            key={tab.id}
                            onClick={() => handleTabChange(tab.id)}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                                activeTab === tab.id
                                    ? 'text-brand-700 ring-1 ring-brand-300 bg-primary'
                                    : 'text-tertiary hover:text-primary hover:bg-secondary'
                            }`}
                        >
                            {tab.id !== 'all' && tab.icon && <tab.icon className="size-3.5" />}
                            {tab.label}
                            {count > 0 && (
                                <span className={`text-xs rounded-full px-1.5 ${
                                    activeTab === tab.id ? 'bg-brand-50 text-brand-700' : 'bg-secondary text-tertiary'
                                }`}>
                                    {count}
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>

            {/* Activity list */}
            <div className="space-y-2">
                {pageGroups.length === 0 ? (
                    <p className="text-sm text-tertiary py-4 text-center">
                        No items in this category
                    </p>
                ) : (
                    pageGroups.map(({ activity, occurrences }) => (
                        <div key={activity.activity_id}>
                            {occurrences > 1 && (
                                <span className="text-xs text-tertiary mb-1 inline-block">
                                    ×{occurrences} occurrences
                                </span>
                            )}
                            <div className="rounded-lg bg-primary p-3 ring-1 ring-secondary">
                                <ExpandableActivityCard
                                    activity={activity}
                                    isContainerContext={false}
                                    compact
                                />
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-3 border-t border-secondary">
                    <span className="text-xs text-tertiary">
                        {currentPage * PAGE_SIZE + 1}–{Math.min((currentPage + 1) * PAGE_SIZE, filteredGroups.length)} of {filteredGroups.length}
                    </span>
                    <div className="flex items-center gap-1">
                        <button
                            onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                            disabled={currentPage === 0}
                            className="p-1 rounded-md text-tertiary hover:text-primary hover:bg-secondary disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        >
                            <ChevronLeft className="size-4" />
                        </button>
                        {pageNumbers.map((page, idx) =>
                            page === 'ellipsis' ? (
                                <span key={`ellipsis-${idx}`} className="px-1 text-xs text-quaternary">
                                    ...
                                </span>
                            ) : (
                                <button
                                    key={page}
                                    onClick={() => setCurrentPage(page)}
                                    className={`size-7 rounded-md text-xs font-medium transition-colors ${
                                        currentPage === page
                                            ? 'bg-brand-50 text-brand-700'
                                            : 'text-tertiary hover:text-primary hover:bg-secondary'
                                    }`}
                                >
                                    {page + 1}
                                </button>
                            ),
                        )}
                        <button
                            onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
                            disabled={currentPage === totalPages - 1}
                            className="p-1 rounded-md text-tertiary hover:text-primary hover:bg-secondary disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        >
                            <ChevronRight className="size-4" />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
